import { Text, Box, UnorderedList, ListItem, Heading } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { DesktopTable as Table } from 'components/Table';
import ExportMenuFiltered from 'containers/Reports/shared/ExportMenuFiltered';
import { useUserObject } from 'store/useUserStore';
import { formatCurrency, formatDistanceUnit, formatYearMonthDayTime } from 'utils/format';
import { convertSecondsToMinutesRoundDown, isDistanceInMiles } from 'utils/helpers';

const ReportsByDateRangeFilterTable: React.FC<{ data: any; dateRange: Array<any> }> = ({ data, dateRange }) => {
  const { t } = useTranslation(['reports', 'common']);
  const userStore = useUserObject();

  const columns = useMemo(
    () => [
      {
        Header: t('common:name'),
        accessor: 'first_name',
        Cell: (data: CellValue) => <Text>{data.row.original.first_name + ' ' + data.row.original.last_name}</Text>,
      },
      ...(userStore?.companyInfo?.show_employee_id
        ? [{ Header: t('common:userID'), accessor: 'employee_id', width: 120 }]
        : []),
      {
        Header: t('common:jobOrTask'),
        accessor: 'job_name',
        Cell: (data: CellValue) => (
          <>
            {data.value || '- '}
            {data.row.original?.task_name ? '/ ' + data.row.original?.task_name : null}
          </>
        ),
      },
      {
        Header: t('common:clockIn/Out'),
        width: 200,
        accessor: 'clock_in',
        Cell: (data: CellValue) => {
          return (
            <>
              {data.value?.local_time ? formatYearMonthDayTime(data.value?.local_time) : '- '}
              {' - '}
              <br />
              {data.row.original?.clock_out?.local_time
                ? formatYearMonthDayTime(data.row.original?.clock_out?.local_time)
                : ''}
            </>
          );
        },
      },
      {
        Header: `${t('common:paidBreaks')} (${t('common:mins')})`,
        accessor: 'breaks_in_seconds',
        width: 150,
        Cell: (data: CellValue) => (
          <Text>
            {isNaN(convertSecondsToMinutesRoundDown(data.row.original?.paid_break_in_seconds))
              ? '-'
              : convertSecondsToMinutesRoundDown(data.row.original?.paid_break_in_seconds)}
          </Text>
        ),
      },
      {
        Header: `${t('common:unpaidBreaks')} (${t('common:mins')})`,
        accessor: 'unpaid_break_in_seconds',
        width: 150,
        Cell: (data: CellValue) => (
          <Text>
            {isNaN(convertSecondsToMinutesRoundDown(data.row.original?.unpaid_break_in_seconds))
              ? '-'
              : convertSecondsToMinutesRoundDown(data.row.original?.unpaid_break_in_seconds)}
          </Text>
        ),
      },
      {
        Header: t('common:duration'),
        accessor: 'duration',
        width: 120,
        Cell: (data: CellValue) => <Text>{data.row.original?.payrolls?.duration || '- '}</Text>,
      },
      ...(userStore?.companyInfo?.show_mileage
        ? [
            {
              Header: t('common:miles/km'),
              accessor: 'mileage',
              Cell: (data: CellValue) => <Text>{data.value ? formatDistanceUnit(+data.value) : 0}</Text>,
            },
          ]
        : []),
      {
        Header: t('reports:regularHrs'),
        accessor: 'regularTime',
        width: 120,
        Cell: (data: CellValue) => <Text>{data.row.original?.payrolls?.regular_time || '- '}</Text>,
      },
      {
        Header: t('reports:regularPay'),
        width: 120,
        accessor: 'regularCost',
        Cell: (data: CellValue) => <Text>{data.row.original?.payrolls?.regular_cost || '- '}</Text>,
      },
      ...(userStore?.companyInfo?.daily_overtime
        ? [
            {
              Header: t('reports:OTHrs'),
              accessor: 'overtime',
              width: 120,
              Cell: (data: CellValue) => <Text>{data.row.original?.payrolls?.overtime || '- '}</Text>,
            },
          ]
        : []),
      ...(userStore?.companyInfo?.daily_double_overtime
        ? [
            {
              Header: t('reports:DOThrs'),
              accessor: 'doubleOvertime',
              width: 120,
              Cell: (data: CellValue) => <Text>{data.row.original?.payrolls?.double_overtime || '- '}</Text>,
            },
          ]
        : []),
      ...(userStore?.companyInfo?.show_timesheet_notes
        ? [
            {
              Header: t('common:notes'),
              accessor: 'notes',
            },
          ]
        : []),
    ],
    [userStore, t]
  );

  return (
    <Box p="4">
      <Heading variant="h2" size="md" fontWeight="semibold" mb="4">
        {t('reports:filteredReport')}
      </Heading>
      {data?.attributes ? <ExportMenuFiltered attributes={data?.attributes} dateRange={dateRange} /> : null}
      <Table columns={columns} data={data?.timesheets || []} />
      <Box>
        <UnorderedList
          ml="0"
          listStyleType="none"
          color="inherit"
          textAlign="right"
          sx={{ '& li': { padding: '2', borderBottom: '1px solid', borderColor: 'gray.300', fontWeight: 'semibold' } }}
        >
          <ListItem>
            <Box display="inline-grid" gridTemplateColumns="1fr 100px">
              <span>{t('reports:totalHours')}:</span>
              <span> {data?.total?.total_hour}</span>
            </Box>
          </ListItem>
          <ListItem>
            <Box display="inline-grid" gridTemplateColumns="1fr 100px">
              <span> {isDistanceInMiles() ? t('reports:totalMileage') : t('reports:totalKilometers')}:</span>
              <span> {data?.total?.total_mileage ? formatDistanceUnit(+data?.total?.total_mileage) : ''}</span>
            </Box>
          </ListItem>
          <ListItem>
            <Box display="inline-grid" gridTemplateColumns="1fr 100px">
              <span>{t('reports:totalCost')}:</span>
              <span> {data?.total?.total_cost ? formatCurrency(+data?.total?.total_cost) : ''}</span>
            </Box>
          </ListItem>
          <ListItem>
            <Box display="inline-grid" gridTemplateColumns="1fr 100px">
              <span>{isDistanceInMiles() ? t('reports:totalMileageCost') : t('reports:totalKilometersCost')}:</span>
              <span> {data?.total?.mileage_cost ? formatCurrency(+data?.total?.mileage_cost) : ''}</span>
            </Box>
          </ListItem>
        </UnorderedList>
      </Box>
    </Box>
  );
};

export default ReportsByDateRangeFilterTable;
